import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Tag } from "lucide-react";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, removeFromCart, updateQty, format, cartSubtotalCHF, discount, cartDiscountCHF, applyDiscount, clearDiscount, t, lang, isRTL } = useApp();
  const [code, setCode] = useState("");

  const apply = async () => {
    if (!code.trim()) return;
    const res = await applyDiscount(code.trim().toUpperCase(), cartSubtotalCHF);
    if (res.ok) { toast.success(t("cart_discount_applied") + `: ${res.data.code}`); setCode(""); }
    else toast.error(t("cart_discount_invalid"));
  };

  const total = Math.max(0, cartSubtotalCHF - cartDiscountCHF);

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side={isRTL ? "left" : "right"} className="bg-[#0A0A0A] border-white/10 text-white w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl text-white">{t("cart_title")}</SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-white/60">
            <p data-testid="cart-empty">{t("cart_empty")}</p>
            <Link to="/shop" onClick={() => setCartOpen(false)} className="btn-outline">{t("nav_shop")}</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4 py-4">
              {cart.map((it) => {
                const name = it.name?.[lang] || it.name?.de || it.slug;
                return (
                  <div key={`${it.product_id}-${it.size||""}-${it.variant||""}`} className="flex gap-4 pb-4 border-b border-white/5">
                    <div className="w-20 h-20 bg-white/5 shrink-0 overflow-hidden">
                      {it.image && <img src={it.image} alt={name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm">{name}</div>
                        <button onClick={() => removeFromCart(it.product_id, it.size, it.variant)} data-testid={`cart-remove-${it.slug}`} className="text-white/50 hover:text-gold" aria-label={t("aria_remove")}>
                          <X size={16} />
                        </button>
                      </div>
                      {(it.size || it.variant) && <div className="text-xs text-white/50 mt-1">{[it.size, it.variant].filter(Boolean).join(" · ")}</div>}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-white/15">
                          <button onClick={() => updateQty(it.product_id, it.size, it.variant, it.quantity - 1)} className="px-2 py-1 hover:text-gold"><Minus size={12} /></button>
                          <span className="px-3 text-sm">{it.quantity}</span>
                          <button onClick={() => updateQty(it.product_id, it.size, it.variant, it.quantity + 1)} className="px-2 py-1 hover:text-gold"><Plus size={12} /></button>
                        </div>
                        <div className="text-sm text-gold">{format(it.price_chf * it.quantity)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute top-3 start-3 text-white/40" />
                  <input value={code} onChange={e => setCode(e.target.value)} placeholder={t("cart_discount_code")} className="ps-9" data-testid="cart-discount-input" />
                </div>
                <button onClick={apply} className="btn-outline shrink-0" data-testid="cart-discount-apply">{t("cart_apply")}</button>
              </div>
              {discount && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gold">{discount.code} · -{format(cartDiscountCHF)}</span>
                  <button onClick={clearDiscount} className="text-white/50 hover:text-gold uppercase tracking-[0.15em]">×</button>
                </div>
              )}
              <div className="flex justify-between text-sm text-white/70">
                <span>{t("cart_subtotal")}</span>
                <span data-testid="cart-subtotal" className="text-white">{format(cartSubtotalCHF)}</span>
              </div>
              {cartDiscountCHF > 0 && (
                <div className="flex justify-between text-sm text-gold">
                  <span>{t("cart_discount")}</span>
                  <span>-{format(cartDiscountCHF)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-white/50 italic">{t("cart_tax_note")}</div>
              <div className="flex justify-between border-t border-white/10 pt-3 text-white">
                <span>{t("cart_total")}</span>
                <span className="text-gold font-serif-display text-xl">{format(total)}</span>
              </div>
              <Link to="/checkout" onClick={() => setCartOpen(false)} className="btn-gold w-full" data-testid="cart-checkout-btn">
                {t("cart_checkout")}
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
